/**
 * A labelled form row for the composer.
 *
 * `@flint/ui`'s `Field` is a layout wrapper with `helper`/`error` — it has no
 * `label` prop. Passing one spread it onto the div, so these fields rendered
 * with no visible label and no accessible name at all. This pairs a real
 * `<label htmlFor>` with the control, which is what makes them
 * `getByLabel`-able (design 43 AD2's a11y line).
 */
import { useId, type ReactNode } from "react";
import { Field, Label } from "@flint/ui";

export function LabelledField({
  label,
  hint,
  helper,
  children,
}: {
  label: string;
  /** Right-aligned adornment — the character counters live here. */
  hint?: ReactNode;
  helper?: ReactNode;
  /** Receives the generated id so the label actually points at the control. */
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <Field helper={helper}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {hint}
      </div>
      {children(id)}
    </Field>
  );
}
